import { useState, useMemo, useEffect, useCallback } from 'react'
import { any } from '@axilon-apps/libs-shared-utils'
import { Card } from '@axilon-apps/libs-ui-shadcn'
import type { Model } from '@axilon-apps/domains-identity/types'
import {
  DataTable,
  TableSkeletonLoader,
  Pagination,
  EmptyTable,
  useToggle,
  useConfirmModal
} from '@axilon-apps/libs-ui-components'
import {
  usePermissions,
  useSelector,
  getCurrentUserSelector
} from '@axilon-apps/domains-identity/state'
import type { ColumnDef } from '@axilon-apps/libs-ui-components'
import { IdentityAction } from '@axilon-apps/domains-identity/constants'

import UsersTableColumnsConfig, { type ExtraRowAction } from './users-table-columns'
import ChangePasswordModal from './components/ChangePasswordModal/ChangePasswordModal'
import RenameUserModal from './components/RenameUserModal/RenameUserModal'
import ViewUserUuidModal from './components/ViewUserUuidModal/ViewUserUuidModal'
import useSetUserEnabled from '../../hooks/use-set-user-enabled'

interface Props {
  users: Model.User[]
  async: {
    isLoading: boolean
    isFetching: boolean
  }
  pagination: {
    paginate: (page: number, perPage: number) => void
    page: number
    perPage: number
    total: number
  }
  extraActions?: ExtraRowAction[]
  extraColumns?: ColumnDef<Model.User>[]
  showEmail: boolean
}

const UsersTable = ({
  users,
  async: { isLoading, isFetching },
  pagination,
  extraActions,
  extraColumns,
  showEmail
}: Props) => {
  const passwordModal = useToggle()
  const renameModal = useToggle()
  const uuidModal = useToggle()
  const { can, isGlobalAdmin } = usePermissions()
  const canRenameUser = can(IdentityAction.Users.Update)
  const canResetPassword = can(IdentityAction.Users.UpdatePassword)
  const canManageStatus = can(IdentityAction.Users.Disable)
  const currentUserId = useSelector(getCurrentUserSelector)?.id ?? ''
  const { disable, enable } = useSetUserEnabled()
  const disableConfirm = useConfirmModal<Model.User>()
  const enableConfirm = useConfirmModal<Model.User>()
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedUsername, setSelectedUsername] = useState('')
  const [renameUser, setRenameUser] = useState<Model.User | null>(null)
  const [uuidUser, setUuidUser] = useState<Model.User | null>(null)
  const [previousRows, setPreviousRows] = useState<Model.User[]>([])

  const hasData = any(users)
  const isLoadingState = isLoading || isFetching
  const showSkeleton = isLoadingState && !previousRows.length

  const displayRows = showSkeleton ? previousRows : users

  const onChangePassword = useCallback(
    ({ id, username }: Model.User) => {
      setSelectedUserId(id)
      setSelectedUsername(username)
      passwordModal.open()
    },
    [passwordModal]
  )

  const onRenameUser = useCallback(
    (user: Model.User) => {
      setRenameUser(user)
      renameModal.open()
    },
    [renameModal]
  )

  const onViewUuid = useCallback(
    (user: Model.User) => {
      setUuidUser(user)
      uuidModal.open()
    },
    [uuidModal]
  )

  const onDisableUser = useCallback(
    (user: Model.User) => disableConfirm.openConfirm(user),
    [disableConfirm]
  )

  const onEnableUser = useCallback(
    (user: Model.User) => enableConfirm.openConfirm(user),
    [enableConfirm]
  )

  const columns = useMemo(
    () =>
      UsersTableColumnsConfig({
        onChangePassword,
        onRenameUser,
        onViewUuid,
        onDisableUser,
        onEnableUser,
        canRenameUser,
        canResetPassword,
        canManageStatus,
        isGlobalAdmin,
        currentUserId,
        hasData,
        extraActions,
        extraColumns,
        showEmail
      }),
    [
      onChangePassword,
      onRenameUser,
      onViewUuid,
      onDisableUser,
      onEnableUser,
      canRenameUser,
      canResetPassword,
      canManageStatus,
      isGlobalAdmin,
      currentUserId,
      hasData,
      extraActions,
      extraColumns,
      showEmail
    ]
  )

  useEffect(() => {
    if (hasData) {
      setPreviousRows(users)
    }
  }, [users, hasData])

  return (
    <>
      <Card className="flex justify-center overflow-x-auto p-0">
        <DataTable<Model.User> columns={columns} rows={displayRows} stripe="even" />
        {showSkeleton && <TableSkeletonLoader columns={columns.length} hasAvatar={true} />}
        {!showSkeleton && !hasData && <EmptyTable />}
      </Card>
      {hasData && (
        <Pagination
          {...{
            onPagination: pagination.paginate,
            total: pagination.total,
            page: pagination.page,
            perPage: pagination.perPage
          }}
        />
      )}
      {passwordModal.isOpen && (
        <ChangePasswordModal
          close={passwordModal.close}
          isOpen={passwordModal.isOpen}
          userId={selectedUserId}
          username={selectedUsername}
        />
      )}
      {renameModal.isOpen && renameUser && (
        <RenameUserModal
          isOpen={renameModal.isOpen}
          close={renameModal.close}
          user={renameUser}
          showEmail={showEmail}
        />
      )}
      {uuidModal.isOpen && uuidUser && (
        <ViewUserUuidModal isOpen={uuidModal.isOpen} close={uuidModal.close} user={uuidUser} />
      )}
      <disableConfirm.ConfirmationModal
        title="Disable account"
        description="This will revoke all active sessions and prevent the user from logging in until the account is re-enabled."
        confirmButton="Disable"
        onConfirm={(user) => disable(user.id, user.username)}
      ></disableConfirm.ConfirmationModal>
      <enableConfirm.ConfirmationModal
        title="Enable account"
        description="This will restore the user's ability to log in."
        confirmButton="Enable"
        onConfirm={(user) => enable(user.id, user.username)}
      />
    </>
  )
}

export default UsersTable
